// index.tsx

import { RefObject, useEffect, useRef } from 'react';
import { Dimensions, DimensionProps, useResizeObserverCore } from './useResizeObserverCore';

/**
 * Hook for Web that listens for size changes via the ResizeObserver API.
 *
 * @returns {[Dimensions, DimensionProps<T>]} The current dimensions and the prop object.
 */
export function useResizeObserver<T extends Element = HTMLDivElement>(): [
  Dimensions, 
  DimensionProps<T>
] {
  const [dimensions, updateDimensions] = useResizeObserverCore();
  const ref = useRef<T>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }

    // Initialize with current dimensions if the element exists
    const { clientWidth, clientHeight } = element;
    updateDimensions(clientWidth, clientHeight);

    // Only set up ResizeObserver if available
    if (typeof window.ResizeObserver === 'undefined') {
        console.warn('ResizeObserver not supported in this environment. Dimensions will not update on resize.');
        return;
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      const { width, height } = entry.contentRect;

      updateDimensions(width, height);
    });

    observer.observe(element);

    // Cleanup function
    return () => {
      observer.unobserve(element);
      observer.disconnect();
    };
  }, [updateDimensions]);

  // On Web, we return the ref
  const dimensionProps: DimensionProps<T> = {
    ref: ref as RefObject<T>,
  };

  return [dimensions, dimensionProps];
}